import {
	createFileRoute,
	Link,
	Outlet,
	useLocation,
} from "@tanstack/react-router";
import { useRole } from "@/hooks/use-role";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/_settings")({
	component: SettingsLayout,
});

// Tab visibility is role-gated (FR-005: nav items hidden for inaccessible
// features), matched to the lowest role the backend lets edit that surface:
//  - `ownerOnly`     → account owner only (Billing is candidate-scoped).
//  - `adminOnly`     → admin only: Team (user management) and Fundraising
//    settings (Anedot connect, FEC compliance — admin per AME-531).
//  - `readWriteOnly` → readwrite+admin: Email settings (PATCH sender defaults is
//    `readwrite`; provision/disconnect inside the page are gated to admin).
//  - Campaign Goals stays visible to readonly+.
const settingsTabs = [
	{ label: "Campaign Goals", to: "/settings", adminOnly: false, readWriteOnly: false, ownerOnly: false },
	{ label: "Team", to: "/team", adminOnly: true, readWriteOnly: false, ownerOnly: false },
	{ label: "Email", to: "/email-settings", adminOnly: false, readWriteOnly: true, ownerOnly: false },
	{ label: "Fundraising", to: "/fundraising-settings", adminOnly: true, readWriteOnly: false, ownerOnly: false },
	{ label: "Billing", to: "/billing", adminOnly: false, readWriteOnly: false, ownerOnly: true },
] as const;

function SettingsLayout() {
	const location = useLocation();
	const { isAdmin, isReadWrite, isOwner } = useRole();
	const visibleTabs = settingsTabs.filter(
		(tab) =>
			(!tab.adminOnly || isAdmin) &&
			(!tab.readWriteOnly || isReadWrite) &&
			(!tab.ownerOnly || isOwner),
	);

	return (
		<div>
			<div className="space-y-4 px-6 pt-6">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Settings</h1>
					<p className="text-muted-foreground">
						Manage your campaign configuration, team, and integrations.
					</p>
				</div>

				<nav className="flex gap-1 border-b">
					{visibleTabs.map((tab) => {
						const isActive = location.pathname === tab.to;
						return (
							<Link
								key={tab.to}
								to={tab.to}
								className={cn(
									"px-4 py-2 text-sm font-medium transition-colors",
									"border-b-2 -mb-px",
									isActive
										? "border-primary text-foreground"
										: "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30",
								)}
							>
								{tab.label}
							</Link>
						);
					})}
				</nav>
			</div>

			<Outlet />
		</div>
	);
}
